import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { getLocalName } from 'vet-bones/utils';
import { createSelectItem } from 'vet-bones/utils/select';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM } from 'src/constants/project';
import { ROUTE } from 'src/constants/routes';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useGenerateNewIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useGenerateNewIri';
import { addAttributeItemToClass } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useModelConceptAttributeForClass';
import { useCompleteRelationshipMappingsForClassMapping } from 'src/containers/ProjectDiagram/hooks/useCompleteRelationshipMappingsForClassMapping';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  MappingField,
  MappingIdentifierPart,
  MappingIdentifierType,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { createDraftData } from 'src/containers/ProjectDiagram/utils/draft/createDraftData';
import {
  getAllDomainsAndRanges,
  isValidMappingDomainAndRange,
} from 'src/containers/ProjectDiagram/utils/isValidDomainAndRange';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addMapping } from 'src/containers/ProjectDiagram/utils/mapping/addMapping';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addRelationshipData } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addModelConceptRelationship';
import { addProjectModelIdsToIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';
import {
  getResourceKey,
  getResourceValue,
} from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { DEFAULT_DELIMITER } from 'src/containers/ProjectMapping/hooks/compositePrimaryIdentifiers';
import {
  RelationshipSuggestion,
  Suggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import { MappingSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { XSD_STRING_IRI } from 'src/templates/queries/iris';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useCreateMappings = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const generateNewIri = useGenerateNewIri();
  const history = useHistory();
  const isUsingMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));
  const navigateMapping = useNavigateMapping();

  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );

  const handleCompleteRelationshipMappings =
    useCompleteRelationshipMappingsForClassMapping();

  return React.useCallback(
    async (
      mappingSuggestions: MappingSuggestion[] | null,
      skipNavigation = false
    ) => {
      if (!diagram || !mappingSuggestions) {
        return diagram;
      }

      const projectModelIds = targetModelId ? [targetModelId] : [];

      let lastMappingUuid = null;
      let numMappingsAdded = 0;

      if (!diagram.model.modelData.draftData) {
        // clear undo history for new draft
        diagram.undoManager.clear();
      }
      await commitDiagramModel(
        diagram,
        (model, diagram) => {
          // setup draft data
          if (!model.modelData.draftData) {
            const draftData = createDraftData(model);
            setDataProperty<ModelData>(model)(
              model.modelData,
              'draftData',
              draftData
            );
          }

          // record is resource id -> class iri -> class mapping key
          const createdClassMappingsByResource: Record<
            string,
            Record<string, Mapping>
          > = {};

          const classesToCreate: Record<
            string,
            Parameters<typeof addModelConceptClass>[0]
          > = {};
          const getCreatedClassIri = (iri: string) =>
            classesToCreate[iri]?.iri || iri;

          const relationshipIrisToCreate: Record<string, string> = {};
          const getCreatedRelationshipIri = (iri: string) =>
            relationshipIrisToCreate[iri] || iri;

          const attributeIrisToCreate: Record<string, string> = {};
          const getCreatedAttributeIri = (iri: string) =>
            attributeIrisToCreate[iri] || iri;

          const relationshipMappingsToCreate: RelationshipSuggestion[] = [];

          // create new classes
          mappingSuggestions.forEach((mappingSuggestion) => {
            if (!mappingSuggestion.isClassAutoCreated) {
              return;
            }

            // create a new iri for the class if not yet generated
            if (!classesToCreate[mappingSuggestion.classIri]) {
              const newClassIri = generateNewIri(mappingSuggestion.classIri);
              classesToCreate[mappingSuggestion.classIri] = {
                iri: newClassIri,
                label: mappingSuggestion.classLabel,
                projectModelIds,
              };
            }

            mappingSuggestion.suggestions.forEach((suggestion) => {
              if (
                suggestion.type === SuggestionType.RELATIONSHIP &&
                suggestion.propertyType
              ) {
                // create a new iri for the opposing class if not yet generated
                if (!classesToCreate[suggestion.propertyType]) {
                  classesToCreate[suggestion.propertyType] = {
                    iri: generateNewIri(suggestion.propertyType),
                    label:
                      suggestion.propertyTypeLabel ||
                      getLocalName(suggestion.propertyType),
                    projectModelIds,
                  };
                }
              }
            });
          });
          const classIris = new Set(
            Object.values(classesToCreate).map(({ iri }) => iri)
          );

          // create new relationships without reusing classs IRIs
          mappingSuggestions.forEach((mappingSuggestion) => {
            if (!mappingSuggestion.isClassAutoCreated) {
              return;
            }

            mappingSuggestion.suggestions.forEach((suggestion) => {
              if (
                suggestion.type === SuggestionType.RELATIONSHIP &&
                suggestion.propertyType
              ) {
                // create a new iri for the property if not yet generated
                if (!relationshipIrisToCreate[suggestion.iri]) {
                  relationshipIrisToCreate[suggestion.iri] = generateNewIri(
                    suggestion.iri,
                    classIris
                  );
                }
              }
            });
          });
          const classAndRelationshipIris = new Set([
            ...classIris,
            ...Object.values(relationshipIrisToCreate),
          ]);

          // create new attributes without reusing class or relationship IRIs
          mappingSuggestions.forEach((mappingSuggestion) => {
            if (!mappingSuggestion.isClassAutoCreated) {
              return;
            }

            mappingSuggestion.suggestions.forEach((suggestion) => {
              if (suggestion.type === SuggestionType.ATTRIBUTE) {
                // create a new iri for the property if not yet generated
                if (!attributeIrisToCreate[suggestion.iri]) {
                  attributeIrisToCreate[suggestion.iri] = generateNewIri(
                    suggestion.iri,
                    classAndRelationshipIris
                  );
                }
              }
            });
          });

          mappingSuggestions.forEach((mappingSuggestion) => {
            const resourceData = model.nodeDataArray.find(
              (nodeData): nodeData is Resource =>
                isResource(nodeData) &&
                nodeData.key === getResourceKey(mappingSuggestion.resourceId)
            );
            if (!resourceData) {
              // skip missing resources
              return;
            }
            const fields = resourceData.fields.reduce<
              Record<string, MappingField>
            >((acc, { value, label }) => {
              acc[value] = { value, label, targets: [] };
              return acc;
            }, {});

            let labelField: string | undefined;
            const primaryIdentifierParts: MappingIdentifierPart[] = [];

            const hasField = (field: string) => Boolean(fields[field]);
            const hasFields = (fields: MappingIdentifierPart[]) =>
              fields.every(
                (field) =>
                  field.type !== MappingIdentifierType.FIELD ||
                  hasField(field.value)
              );

            // Populate field targets for each suggestion
            mappingSuggestion.suggestions.forEach((suggestion: Suggestion) => {
              if (suggestion.rejected) {
                // skip rejected suggestions
                return;
              }

              if (
                suggestion.type === SuggestionType.PRIMARY_IDENTIFIER &&
                hasFields(suggestion.fields)
              ) {
                suggestion.fields.forEach((primaryIdentifierFields) => {
                  if (
                    primaryIdentifierFields.type ===
                      MappingIdentifierType.FIELD &&
                    primaryIdentifierParts.length &&
                    primaryIdentifierParts[primaryIdentifierParts.length - 1]
                      .type === MappingIdentifierType.FIELD
                  ) {
                    primaryIdentifierParts.push({
                      type: MappingIdentifierType.DELIMITER,
                      value: DEFAULT_DELIMITER,
                    });
                  }
                  primaryIdentifierParts.push(primaryIdentifierFields);
                });
              } else if (
                suggestion.type === SuggestionType.LABEL &&
                hasField(suggestion.field)
              ) {
                labelField = suggestion.field;
              } else if (
                suggestion.type === SuggestionType.ATTRIBUTE &&
                hasField(suggestion.field)
              ) {
                fields[suggestion.field].targets.push(
                  getCreatedAttributeIri(suggestion.iri)
                );
              } else if (
                suggestion.type === SuggestionType.RELATIONSHIP &&
                suggestion.propertyType &&
                hasFields(suggestion.fields)
              ) {
                relationshipMappingsToCreate.push(suggestion);
              }
            });

            const classIri = getCreatedClassIri(mappingSuggestion.classIri);

            if (mappingSuggestion.isClassAutoCreated) {
              // create the new model concept class
              addModelConceptClass(classesToCreate[mappingSuggestion.classIri])(
                model
              );

              mappingSuggestion.suggestions.forEach((suggestion) => {
                if (suggestion.rejected) {
                  return;
                }

                if (suggestion.type === SuggestionType.ATTRIBUTE) {
                  const { iri, label, propertyType } = suggestion;
                  const attributePropertyType = propertyType || XSD_STRING_IRI;
                  // create the new class attribute
                  addAttributeItemToClass(classIri, {
                    text: label || getLocalName(iri),
                    value: getCreatedAttributeIri(iri),
                    isNewlyCreated: true,
                    ranges: [
                      createSelectItem(
                        attributePropertyType,
                        attributePropertyType
                      ),
                    ],
                  })(model);
                }

                if (
                  suggestion.type === SuggestionType.RELATIONSHIP &&
                  suggestion.propertyType
                ) {
                  const { iri, label, propertyType, direction } = suggestion;
                  const [fromIri, toIri] =
                    direction === RelationshipDirection.OUTGOING
                      ? [classIri, getCreatedClassIri(propertyType)]
                      : [getCreatedClassIri(propertyType), classIri];

                  // create the new opposing class
                  addModelConceptClass(classesToCreate[propertyType])(model);
                  // create the new relationship
                  const value = getCreatedRelationshipIri(iri);
                  addRelationshipData({
                    fromIri,
                    value,
                    toIri,
                    model,
                  });
                  // set the label for the new relationship (not done in addRelationshipData)
                  setLabelForIri(value, label || getLocalName(iri))(model);
                  addProjectModelIdsToIri(value, projectModelIds)(model);
                }
              });
            }

            lastMappingUuid = uuid();
            numMappingsAdded += 1;

            // add the new class mapping
            const { mapping } = addMapping({
              fromId: mappingSuggestion.resourceId,
              toKey: getModelConceptClassKey(classIri),
              text: mappingSuggestion.name,
              fields,
              label: labelField,
              primaryIdentifier: {
                baseIri: null,
                parts: primaryIdentifierParts,
              },
              value: lastMappingUuid,
            })(model, diagram);
            if (mapping) {
              const resourceId = getResourceValue(resourceData.key);
              if (!createdClassMappingsByResource[resourceId]) {
                createdClassMappingsByResource[resourceId] = {};
              }
              createdClassMappingsByResource[resourceId][
                // key on the orignial classIri
                mappingSuggestion.classIri
              ] = mapping;
            }
          });

          const { classMappingIriByKey, domainsAndRanges } =
            getAllDomainsAndRanges(model);

          relationshipMappingsToCreate.forEach((suggestion) => {
            const { resource, propertyType } = suggestion;
            const classMapping =
              createdClassMappingsByResource[resource.id]?.[
                suggestion.classIri
              ];
            if (!classMapping || !propertyType) {
              return;
            }

            const option =
              suggestion.direction === RelationshipDirection.OUTGOING
                ? {
                    from: classMapping.key,
                    relationshipIri: getCreatedRelationshipIri(suggestion.iri),
                    toClassIri: getCreatedClassIri(propertyType),
                    toPrimaryIdentifier: {
                      baseIri: null,
                      parts: [...suggestion.fields],
                    },
                  }
                : {
                    fromClassIri: getCreatedClassIri(propertyType),
                    fromPrimaryIdentifier: {
                      baseIri: null,
                      parts: [...suggestion.fields],
                    },
                    relationshipIri: getCreatedRelationshipIri(suggestion.iri),
                    to: classMapping.key,
                  };

            if (
              !isValidMappingDomainAndRange(
                option,
                classMappingIriByKey,
                domainsAndRanges
              )
            ) {
              console.warn(
                `Skipping create invalid relationship mapping (iri: ${
                  option.relationshipIri
                }, from: ${
                  option.from
                    ? classMappingIriByKey.get(option.from)
                    : option.fromClassIri
                }, to: ${
                  option.to
                    ? classMappingIriByKey.get(option.to)
                    : option.toClassIri
                })`
              );
              return;
            }

            // add the new relationship
            addRelationshipMapping(option)(model);
          });

          // create relationship mappings for newly created class mappings
          const createdClassMappingIds = new Set<string>();
          Object.values(createdClassMappingsByResource).forEach(
            (classMappingsById) => {
              Object.keys(classMappingsById).forEach((mappingId) => {
                createdClassMappingIds.add(mappingId);
              });
            }
          );
          Object.values(createdClassMappingsByResource).forEach(
            (mappingById) => {
              Object.values(mappingById).forEach((mapping) => {
                handleCompleteRelationshipMappings(mapping)(model);
              });
            }
          );
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
      diagram.updateAllTargetBindings();
      diagram.layout.doLayout(diagram);

      dispatch(projectsActionCreators.addedSuggestionsToProject(projectId));

      if (isUsingMappingRoute && !skipNavigation) {
        if (numMappingsAdded === 1) {
          // Navigate to new mapping when only one was created
          navigateMapping(lastMappingUuid, false);
        }
        history.replace(
          `${history.location.pathname}?${PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM}`
        );
      }

      return diagram;
    },
    [
      diagram,
      dispatch,
      generateNewIri,
      handleCompleteRelationshipMappings,
      history,
      isUsingMappingRoute,
      navigateMapping,
      projectId,
      targetModelId,
    ]
  );
};
